import { PropsWithChildren } from 'react';

import { QuickActionTranslation } from '../ConversationQuickAction/ConversationQuickAction.types';
import {
  SvbrgConversationEntityVariant,
  SvbrgQuickActionTranslation,
  SvbrgQuickActionTranslationVariant,
} from '@/generated/gql/graphql';
import { useConversation } from '~/hooks/useConversation/useConversation';

import { ConversationContext, useInitializeConversation } from './ConversationContextProvider.hook';

const filterTranslationsByVariant = (
  translations: SvbrgQuickActionTranslation[],
  variant: SvbrgQuickActionTranslationVariant,
): QuickActionTranslation[] =>
  translations
    .filter((translation) => translation.variant === variant)
    .map(({ id, text, language }) => ({ id, text, language }));

interface ConversationContextProviderProps {
  entityId: string;
  entityVariant: SvbrgConversationEntityVariant;
  title: string;
  skip?: boolean;
}

export const ConversationContextProvider = ({
  entityId,
  entityVariant,
  title,
  skip,
  children,
}: PropsWithChildren<ConversationContextProviderProps>) => {
  const { conversationId, quickActions, messageHistory, resetConversation, loading } = useInitializeConversation({
    entityId,
    entityVariant,
    title,
    skip,
  });

  const { status, messages, error, inputProps, sendMessage, resetStream } = useConversation({
    entityId,
    entityVariant,
    conversationId,
    messageHistory,
    loading,
  });

  const reset = async () => {
    resetStream();
    await resetConversation();
  };

  return (
    <ConversationContext.Provider
      value={{
        entityId,
        entityVariant,
        conversationId,
        status,
        error,
        quickActions: quickActions.map((quickAction) => ({
          ...quickAction,
          titleTranslations: filterTranslationsByVariant(
            quickAction.translations,
            SvbrgQuickActionTranslationVariant.TITLE,
          ),
          helpTextTranslations: filterTranslationsByVariant(
            quickAction.translations,
            SvbrgQuickActionTranslationVariant.HELP_TEXT,
          ),
        })),
        messages,
        inputProps,
        sendMessage,
        reset,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};
